"use client";

import { SalesAgentResponseDTO } from "@alba-cars/common-modules";
import { useRef } from "react";
import type { MouseEvent } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import IconArrowRight from "@/components/Icons/IconArrowRight";
import Container from "@/components/layout/Container";
import SalesAgentCard from "@/components/ui/SalesAgentCard";
import { StaffMember } from "@/lib/api/sales-agent/get-sales-agents";
import "swiper/css";

interface SalesTeamsSwiperProps {
  salesAgents: SalesAgentResponseDTO[];
}

export default function SalesTeamsSwiper({ salesAgents }: SalesTeamsSwiperProps) {
  const swiperRef = useRef<SwiperType | null>(null);

  const handleSlideNext = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    swiperRef.current?.slideNext();
  };

  const handleSlidePrev = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    swiperRef.current?.slidePrev();
  };

  if (!salesAgents || salesAgents.length === 0) {
    return null;
  }

  return (
    <section className="pb-8">
      <Container>
        <div className="relative overflow-visible">
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            className="!pb-2 [&_.swiper-slide]:!flex [&_.swiper-slide]:!h-auto [&_.swiper-wrapper]:!items-stretch"
            preventInteractionOnTransition={true}
            slidesPerView={1}
            spaceBetween={16}
            breakpoints={{
              320: {
                slidesPerView: 1.2,
                spaceBetween: 20,
              },
              640: {
                slidesPerView: 2,
                spaceBetween: 20,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 24,
              },
              1280: {
                slidesPerView: 4,
                spaceBetween: 24,
              },
            }}
          >
            {salesAgents.map((agent, idx) => (
              <SwiperSlide key={`agent-${agent.id}-${idx}`} className="!flex !h-auto">
                <div className="flex h-full w-full px-1 pb-4">
                  <SalesAgentCard agent={agent as StaffMember} showPreviousWorkplaces className="h-full w-full" />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <button
            type="button"
            aria-label="Next sales agent"
            className="absolute -right-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-black shadow-soft-grey lg:flex"
            onClick={handleSlideNext}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <IconArrowRight height="18" width="18" />
          </button>
          <button
            type="button"
            aria-label="Previous sales agent"
            className="absolute -left-4 top-1/2 z-30 hidden h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-black shadow-soft-grey lg:flex"
            onClick={handleSlidePrev}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <IconArrowRight height="18" width="18" className="rotate-180" />
          </button>
          <div className="pointer-events-none absolute -right-10 top-0 z-10 hidden h-full w-40 bg-slider-gradient lg:block" />
        </div>
      </Container>
    </section>
  );
}
